import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle, Clock, Loader2, Mail, RefreshCw } from 'lucide-react';
import * as api from '../lib/api';
import { PRODUCT_DISPLAY, enrichProducts } from '../config/products';
import { BundleActions } from '../components/BundleActions';
import { usePortal } from '../context/PortalContext';
import { usePortalPageMeta } from '../hooks/usePortalPageMeta';
import { trackPurchaseReady } from '../lib/analytics';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 90000;

export function PurchaseCompletePage() {
    const { slug } = useParams();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { actions } = usePortal();
    const [product, setProduct] = useState(null);
    const [email, setEmail] = useState(
        () => searchParams.get('email') || location.state?.email || ''
    );
    const [activationContext, setActivationContext] = useState(() => ({
        deviceCode: searchParams.get('device_code') || '',
        feature: searchParams.get('feature') || '',
        source: searchParams.get('source') || '',
        activationClaim: searchParams.get('activation_claim') || '',
    }));
    const [purchaseResult, setPurchaseResult] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | polling | ready | timeout | missing-context | error
    const [error, setError] = useState(null);

    const storageKey = useMemo(() => `hforgex:checkout:${slug}`, [slug]);

    usePortalPageMeta({
        title: product
            ? `${product.name} Purchase Status — License Portal — HForgeX`
            : 'Purchase Status — License Portal — HForgeX',
        description: product
            ? `Check the status of your ${product.name} purchase and finish activation.`
            : 'Check the status of your purchase and finish activation.',
    });

    useEffect(() => {
        let cancelled = false;

        async function loadContext() {
            try {
                const products = enrichProducts(await api.fetchProducts());
                const found = products.find((p) => p.slug === slug);
                if (!found) {
                    setStatus('error');
                    setError('We could not find this product.');
                    return;
                }

                const stored = readStoredCheckout(storageKey);
                const resolvedEmail =
                    searchParams.get('email') || location.state?.email || stored?.email || '';
                const resolvedActivationContext = {
                    deviceCode: getResolvedContextValue(
                        searchParams.get('device_code'),
                        stored?.deviceCode
                    ),
                    feature: getResolvedContextValue(searchParams.get('feature'), stored?.feature),
                    source: getResolvedContextValue(searchParams.get('source'), stored?.source),
                    activationClaim: getResolvedContextValue(
                        searchParams.get('activation_claim'),
                        stored?.activationClaim
                    ),
                };

                if (cancelled) return;
                setProduct(found);
                setEmail(resolvedEmail);
                setActivationContext(resolvedActivationContext);

                if (!resolvedEmail) {
                    setStatus('missing-context');
                    return;
                }

                setStatus('polling');
            } catch (err) {
                if (!cancelled) {
                    setStatus('error');
                    setError(err.message || 'We could not check your purchase yet.');
                }
            }
        }

        loadContext();
        return () => {
            cancelled = true;
        };
    }, [location.state, searchParams, slug, storageKey]);

    useEffect(() => {
        if (status !== 'polling' || !product || !email) return;

        let cancelled = false;
        const startedAt = Date.now();

        async function poll() {
            try {
                const result = await api.pollPurchaseStatus(
                    email,
                    product.id,
                    activationContext.activationClaim
                );
                if (cancelled) return;

                if (result.status === 'ready') {
                    setPurchaseResult(result);
                    actions.hydrateFromPurchase?.(result);
                    trackPurchaseReady({
                        productSlug: product.slug,
                        productId: product.id,
                        source: activationContext.source,
                        feature: activationContext.feature,
                        hasDeviceCode: Boolean(activationContext.deviceCode),
                    });
                    setStatus('ready');
                    return;
                }

                if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
                    setStatus('timeout');
                }
            } catch (err) {
                if (cancelled) return;
                if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
                    setStatus('error');
                    setError(err.message || 'We could not check your purchase yet.');
                }
            }
        }

        poll();
        const interval = setInterval(poll, POLL_INTERVAL_MS);
        const timeout = setTimeout(() => {
            if (!cancelled) setStatus((current) => (current === 'polling' ? 'timeout' : current));
        }, POLL_TIMEOUT_MS);

        return () => {
            cancelled = true;
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, [actions, activationContext.activationClaim, email, product, status]);

    const productName = product?.name || PRODUCT_DISPLAY[slug]?.tagline || 'your purchase';

    return (
        <div className="animate-fade-in text-center py-6">
            {status === 'loading' && (
                <StatusPanel
                    icon={<Loader2 className="w-10 h-10 mx-auto text-indigo-500 animate-spin" />}
                    title="Checking your purchase"
                    body="We are connecting your Polar payment to your HForgeX license."
                />
            )}

            {status === 'polling' && (
                <StatusPanel
                    icon={<Loader2 className="w-10 h-10 mx-auto text-indigo-500 animate-spin" />}
                    title="Preparing your license"
                    body={`Thanks for buying ${productName}. This usually takes a few seconds; keep this page open while we finish issuing your license email.`}
                />
            )}

            {status === 'ready' && (
                <StatusPanel
                    icon={<CheckCircle className="w-10 h-10 mx-auto text-emerald-500" />}
                    title={
                        activationContext.deviceCode
                            ? 'Your license is ready for this device'
                            : 'Your license is ready'
                    }
                    body={
                        purchaseResult?.sessionToken && activationContext.deviceCode
                            ? `We issued your ${productName} license and registered this device. You can activate the extension from here.`
                            : activationContext.deviceCode
                              ? `We issued your ${productName} license and kept this device code ready for activation. Enter the license key sent to ${email}; the portal will carry this device code into device setup.`
                              : `We issued your ${productName} license and sent the license key to ${email}.`
                    }
                >
                    {purchaseResult?.sessionToken ? (
                        <ReadyActions
                            product={product}
                            purchaseResult={purchaseResult}
                            activationContext={activationContext}
                        />
                    ) : (
                        <>
                            <Link
                                to={buildLookupTarget(activationContext, slug)}
                                state={{
                                    email,
                                    message: activationContext.deviceCode
                                        ? 'Enter the license key from your HForgeX email. This device code is already attached.'
                                        : 'Enter the license key from your HForgeX email.',
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                            >
                                {activationContext.deviceCode
                                    ? 'Connect This Device'
                                    : 'Go to License Lookup'}{' '}
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                            {!activationContext.deviceCode && <DeviceCodeHint />}
                        </>
                    )}
                </StatusPanel>
            )}

            {status === 'timeout' && (
                <StatusPanel
                    icon={<Clock className="w-10 h-10 mx-auto text-amber-500" />}
                    title="Still preparing your license"
                    body={`If your Polar payment completed, your HForgeX license email should arrive shortly at ${email}.`}
                >
                    <ActionRow email={email} activationContext={activationContext} />
                </StatusPanel>
            )}

            {status === 'missing-context' && (
                <StatusPanel
                    icon={<Mail className="w-10 h-10 mx-auto text-indigo-500" />}
                    title="Check your email"
                    body="If your Polar payment completed, HForgeX will send your license key by email. Use that key to open your license portal."
                >
                    <ActionRow email={email} activationContext={activationContext} />
                </StatusPanel>
            )}

            {status === 'error' && (
                <StatusPanel
                    icon={<RefreshCw className="w-10 h-10 mx-auto text-red-500" />}
                    title="We could not check the purchase yet"
                    body={error || 'Please check your email for the HForgeX license key.'}
                >
                    <ActionRow email={email} activationContext={activationContext} />
                </StatusPanel>
            )}
        </div>
    );
}

function ReadyActions({ product, purchaseResult, activationContext }) {
    const hasDevices = (purchaseResult.devices || []).length > 0;
    const extensionId = product?.extensionId || '';

    return (
        <div className="mx-auto max-w-sm space-y-4 text-left">
            {extensionId && activationContext.deviceCode && (
                <BundleActions
                    onIssueBundle={() =>
                        api.issueBundle(purchaseResult.sessionToken, purchaseResult.licenseId)
                    }
                    hasDevices={hasDevices}
                    isRevoked={purchaseResult.licenseStatus === 'revoked'}
                    activationTarget={{
                        extensionId,
                        productName: product?.activationName || product?.name || 'this extension',
                    }}
                    trackingContext={{
                        productSlug: product?.slug || '',
                        productName: product?.activationName || product?.name || '',
                        source: activationContext.source,
                        feature: activationContext.feature,
                    }}
                />
            )}

            <div className="text-center">
                <Link
                    to={buildManageTarget(activationContext, product?.slug)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                    Open License Portal <ArrowRight className="w-4 h-4" />
                </Link>
            </div>
        </div>
    );
}

function StatusPanel({ icon, title, body, children }) {
    return (
        <div>
            <div className="mb-4">{icon}</div>
            <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-2">{title}</h2>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">{body}</p>
            {children}
        </div>
    );
}

function ActionRow({ email, activationContext }) {
    return (
        <Link
            to={buildLookupTarget(activationContext, activationContext?.productSlug)}
            state={{
                email,
                message: activationContext?.deviceCode
                    ? 'Enter the license key from your HForgeX email. This device code is already attached.'
                    : 'Enter the license key from your HForgeX email.',
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
        >
            {activationContext?.deviceCode ? 'Connect This Device' : 'Go to License Lookup'}{' '}
            <ArrowRight className="w-4 h-4" />
        </Link>
    );
}

function DeviceCodeHint() {
    return (
        <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
            After lookup, the portal may ask for your device code. You can copy it from the
            extension license panel.
        </p>
    );
}

function buildLookupTarget(activationContext = {}, productSlug = '') {
    if (!activationContext.deviceCode) return '/lookup';
    const params = new URLSearchParams({ device_code: activationContext.deviceCode });
    if (productSlug) params.set('product', productSlug);
    params.set('intent', 'activate-existing');
    if (activationContext.feature) params.set('feature', activationContext.feature);
    if (activationContext.source) params.set('source', activationContext.source);
    return `/lookup?${params}`;
}

function buildManageTarget(activationContext = {}, productSlug = '') {
    const params = new URLSearchParams();
    if (activationContext.deviceCode) params.set('device_code', activationContext.deviceCode);
    if (productSlug) params.set('product', productSlug);
    if (activationContext.feature) params.set('feature', activationContext.feature);
    if (activationContext.source) params.set('source', activationContext.source);
    const query = params.toString();
    return query ? `/manage?${query}` : '/manage';
}

function readStoredCheckout(storageKey) {
    try {
        return JSON.parse(window.localStorage.getItem(storageKey) || 'null');
    } catch {
        return null;
    }
}

function getResolvedContextValue(searchValue, storedValue = '') {
    const normalizedSearchValue = normalizePlaceholderValue(searchValue);
    if (normalizedSearchValue) return normalizedSearchValue;
    return normalizePlaceholderValue(storedValue);
}

function normalizePlaceholderValue(value) {
    if (typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^\{[A-Z0-9_]+\}$/.test(trimmed)) return '';
    return trimmed;
}
